// src/screens/EditorAvisoScreen.js
// src/screens/EditorAvisoScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import AIAssistantButton from '../components/Shared/AIAssistantButton';
import { 
  IoArrowBack, 
  IoCloudUploadOutline, 
  IoClose, 
  IoImageOutline, 
  IoCheckmarkCircleOutline,
  IoDocumentTextOutline,
  IoTrashOutline,
  IoDocumentAttachOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// removed duplicate

function EditorAvisoScreen() {
  const navigate = useNavigate();
  const { avisoId } = useParams(); // Para modo "Editar"
  const { currentUser, userData } = useAuth();
  const isEditMode = Boolean(avisoId);

  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [tipo, setTipo] = useState('informativo'); // 'informativo', 'mantenimiento', 'evento', 'urgente'
  const [audiencia, setAudiencia] = useState('todos'); // 'todos', 'residentes', 'vigilantes'
  
  // Estados para Alerta Crítica
  const get24HoursFromNowString = () => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const tzoffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzoffset).toISOString().slice(0, 16);
  };
  const [esAlerta, setEsAlerta] = useState(false);
  const [expiraAlerta, setExpiraAlerta] = useState(get24HoursFromNowString());
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Si estamos en modo "Editar", cargar los datos del aviso
  useEffect(() => {
    if (isEditMode && avisoId) {
      setLoading(true);
      const fetchAviso = async () => {
        try {
          const docRef = doc(db, "avisos", avisoId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setTitulo(data.titulo);
            setContenido(data.contenido);
            setTipo(data.tipo || 'informativo');
            setAudiencia(data.audiencia || 'todos');
            setEsAlerta(data.esAlerta || false);
            if (data.fechaExpiracionAlerta) {
              const d = data.fechaExpiracionAlerta.toDate();
              const tzoffset = d.getTimezoneOffset() * 60000;
              setExpiraAlerta(new Date(d.getTime() - tzoffset).toISOString().slice(0, 16));
            } else {
              setExpiraAlerta(get24HoursFromNowString());
            }
            // Carga de adjuntos con soporte de retrocompatibilidad
            if (data.adjuntos) {
              setAdjuntos(data.adjuntos);
            } else if (data.imagenUrl) {
              setAdjuntos([{ nombre: 'Imagen cargada', url: data.imagenUrl, tipo: 'image' }]);
            } else {
              setAdjuntos([]);
            }
          } else {
            setError("No se encontró el aviso.");
          }
        } catch (e) {
          setError("Error al cargar el aviso.");
          console.error(e);
        }
        setLoading(false);
      };
      fetchAviso();
    }
  }, [isEditMode, avisoId]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `avisos_uploads/${currentUser.uid}_${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      // Determinar tipo de adjunto
      let tipo = 'generic';
      const extension = file.name.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
        tipo = 'image';
      } else if (extension === 'pdf') {
        tipo = 'pdf';
      } else if (['doc', 'docx'].includes(extension)) {
        tipo = 'word';
      } else if (['xls', 'xlsx'].includes(extension)) {
        tipo = 'excel';
      }

      setAdjuntos(prev => [...prev, { nombre: file.name, url, tipo }]);
      setUploading(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error al subir archivo.");
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAdjuntos(prev => prev.filter((_, idx) => idx !== indexToRemove));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titulo || !contenido) {
      setError("El título y el contenido son obligatorios.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Para retrocompatibilidad y visualización rápida, extraemos la primera imagen como imagenUrl
      const primeraImagen = adjuntos.find(a => a.tipo === 'image')?.url || null;

      if (isEditMode) {
        // --- Modo Edición: Actualizar ---
        const docRef = doc(db, "avisos", avisoId);
        await updateDoc(docRef, {
          titulo: titulo,
          contenido: contenido,
          tipo: tipo,
          imagenUrl: primeraImagen,
          adjuntos: adjuntos,
          audiencia: audiencia,
          esAlerta: esAlerta,
          fechaExpiracionAlerta: esAlerta ? Timestamp.fromDate(new Date(expiraAlerta)) : null
        });
        setSuccess("Aviso actualizado con éxito.");

      } else {
        // --- Modo Creación: Crear ---
        const nuevoAviso = {
          titulo: titulo,
          contenido: contenido,
          tipo: tipo,
          imagenUrl: primeraImagen,
          adjuntos: adjuntos,
          audiencia: audiencia,
          fechaCreacion: Timestamp.now(), // La fecha/hora actual
          condominioId: userData.condominioId,
          creadoPorUid: currentUser.uid,
          creadoPorNombre: userData.nombre || 'Admin',
          leidoPor: [], // Array vacío al crear
          esAlerta: esAlerta,
          fechaExpiracionAlerta: esAlerta ? Timestamp.fromDate(new Date(expiraAlerta)) : null
        };
        await addDoc(collection(db, "avisos"), nuevoAviso);
        setSuccess("Aviso publicado con éxito.");
      }
      
      setTimeout(() => {
        navigate('/gestion-avisos'); // Regresar a la lista de admin
      }, 1500);

    } catch (e) {
      console.error("Error al guardar aviso: ", e);
      setError("Error al guardar el aviso.");
      setLoading(false);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-avisos" />
      
      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />
        
        <div className="ks-scroll-area">
          <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
      
      {/* Premium Header Banner */}
      <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', position: 'relative', overflow: 'hidden', padding: '25px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 8px 20px rgba(79, 70, 229, 0.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <button onClick={() => navigate('/gestion-avisos')} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', color: 'white', flexShrink: 0 }}>
                      <IoArrowBack size={22} />
                  </button>
                  <div style={{ marginTop: '0' }}>
                      <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>
                          {isEditMode ? 'Editar Aviso' : 'Crear Nuevo Aviso'}
                      </h1>
                      <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>
                          Publica comunicados oficiales para la comunidad
                      </p>
                  </div>
              </div>
          </div>
          <div style={{ position: 'absolute', right: '-15px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
              <IoImageOutline size={150} color="white" />
          </div>
      </div>

      <div style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)', borderRadius: '16px', padding: '30px', border: '1px solid rgba(255, 255, 255, 0.5)', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.04)' }}>
        
        {error && <p style={{ color: '#e11d48', background: '#ffe4e6', padding: '12px 16px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: '500', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><IoClose size={20}/> {error}</p>}
        
        {success && (
          <div style={{ color: '#059669', background: '#d1fae5', padding: '12px 16px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: '500', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IoCheckmarkCircleOutline size={20} /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ color: '#374151', fontWeight: '600', fontSize: '0.95rem' }}>Título del Aviso</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              disabled={loading}
              placeholder="Ej. Mantenimiento de elevadores"
              style={{ padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = '#6366f1'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ color: '#374151', fontWeight: '600', fontSize: '0.95rem' }}>Tipo / Categoría de Aviso</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              disabled={loading}
              style={{ padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = '#6366f1'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            >
              <option value="informativo">📢 Informativo (General)</option>
              <option value="mantenimiento">🛠️ Mantenimiento / Obras</option>
              <option value="evento">📅 Evento / Comunidad</option>
              <option value="urgente">⚠️ Urgente / Importante</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ color: '#374151', fontWeight: '600', fontSize: '0.95rem' }}>Audiencia (¿Quién puede ver esto?)</label>
            <select
              value={audiencia}
              onChange={(e) => setAudiencia(e.target.value)}
              disabled={loading}
              style={{ padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' }}
              onFocus={(e) => e.target.style.borderColor = '#6366f1'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            >
              <option value="todos">Todos (Residentes y Vigilantes)</option>
              <option value="residentes">Solo Residentes e Inquilinos</option>
              <option value="vigilantes">Solo Vigilantes (Caseta / Seguridad)</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: '#fffbeb', border: '1px solid #fef3c7', padding: '20px', borderRadius: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ paddingRight: '10px' }}>
                <strong style={{ display: 'block', fontSize: '0.95rem', color: '#78350f', marginBottom: '4px', textAlign: 'left' }}>Publicar como Alerta Crítica</strong>
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#b45309', lineHeight: '1.4', textAlign: 'left' }}>Las alertas críticas se muestran fijas en la parte superior y con color destacado.</span>
              </div>
              <div
                onClick={() => setEsAlerta(!esAlerta)}
                style={{
                  width: '50px',
                  height: '28px',
                  borderRadius: '15px',
                  background: esAlerta ? '#d97706' : '#cbd5e1',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    top: '3px',
                    left: esAlerta ? '25px' : '3px',
                    transition: 'left 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            </div>

            {esAlerta && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #fde68a', paddingTop: '12px', marginTop: '4px' }}>
                <label style={{ color: '#78350f', fontWeight: '600', fontSize: '0.9rem', display: 'block', textAlign: 'left' }}>Vencimiento de la Alerta (¿Cuándo dejar de fijar?)</label>
                <input
                  type="datetime-local"
                  value={expiraAlerta}
                  onChange={(e) => setExpiraAlerta(e.target.value)}
                  disabled={loading}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #fcd34d', fontSize: '0.95rem', background: 'white', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ color: '#374151', fontWeight: '600', fontSize: '0.95rem' }}>Fotos y Documentos Adjuntos (Opcional)</label>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="*/*" hidden />
            
            <div
              onClick={() => !uploading && fileInputRef.current.click()}
              style={{ border: '2px dashed #cbd5e1', borderRadius: '16px', padding: '30px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', background: '#f8fafc', color: '#64748b', transition: 'all 0.3s' }}
              onMouseOver={(e) => !uploading && (e.currentTarget.style.borderColor = '#6366f1', e.currentTarget.style.background = '#eef2ff', e.currentTarget.style.color = '#4f46e5')}
              onMouseOut={(e) => !uploading && (e.currentTarget.style.borderColor = '#cbd5e1', e.currentTarget.style.background = '#f8fafc', e.currentTarget.style.color = '#64748b')}
            >
              {uploading ? (
                 <span style={{ fontWeight: '600', color: '#6366f1' }}>Subiendo archivo de forma segura...</span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '50%', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                     <IoCloudUploadOutline size={26} />
                  </div>
                  <span style={{ fontWeight: '500', fontSize: '0.92rem' }}>Toca para subir fotos, circulares, PDFs o contratos</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Cualquier formato hasta 10MB</span>
                </div>
              )}
            </div>

            {/* List of uploaded attachments */}
            {adjuntos.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Archivos Cargados ({adjuntos.length})</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                  {adjuntos.map((file, idx) => {
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f1f5f9', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                          {file.tipo === 'image' ? (
                            <IoImageOutline size={20} style={{ color: '#6366f1', flexShrink: 0 }} />
                          ) : (
                            <IoDocumentTextOutline size={20} style={{ color: '#64748b', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.nombre}</span>
                          <span style={{ fontSize: '0.75rem', background: '#cbd5e1', color: '#475569', padding: '2px 8px', borderRadius: '20px', fontWeight: '700', textTransform: 'uppercase' }}>{file.tipo}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(idx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'background 0.2s' }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#fee2e2'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <IoTrashOutline size={18} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ color: '#374151', fontWeight: '600', fontSize: '0.95rem' }}>Mensaje Oficial</label>
              <AIAssistantButton 
                textoOriginal={contenido}
                onSugerenciaAceptada={(nuevoTexto) => setContenido(nuevoTexto)}
                contexto="avisos"
              />
            </div>
            <textarea
              rows={8}
              value={contenido}
              onChange={(e) => setContenido(e.target.value)}
              disabled={loading}
              placeholder="Escribe el cuerpo del aviso..."
              style={{ padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', background: '#f8fafc', transition: 'all 0.2s', outline: 'none', resize: 'vertical' }}
              onFocus={(e) => e.target.style.borderColor = '#6366f1'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>

          <button type="submit" disabled={loading || uploading} style={{ marginTop: '10px', width: '100%', padding: '16px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1.1rem', fontWeight: '700', cursor: (loading || uploading) ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }} onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(99, 102, 241, 0.4)'; }} onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)'; }}>
            {loading ? 'Procesando...' : (isEditMode ? 'Guardar Cambios del Aviso' : 'Publicar Circular Oficial')}
          </button>
        </form>
      </div>
    </div>
        </div>
      </main>
      <KondoBottomNav activeRoute="/gestion-avisos" />
    </div>
  );
}

export default EditorAvisoScreen;
